import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/cloudflare";
import { Link } from "@remix-run/react";
import { motion } from "framer-motion";

import { item, Layout } from "~/components/site/layout";
import { SideNav } from "~/components/site/legal/sidenav";
import { EMAILS, ROUTE_DATA } from "~/lib/constants";
import { getMeta } from "~/lib/get-meta";
import { getUrls } from "~/lib/get-urls";

const { desc, path, title } = ROUTE_DATA.businessUserAgreement;

export function loader({ request }: LoaderFunctionArgs) {
  return {
    urls: getUrls({ path, request }),
  };
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  return data ? getMeta({ desc, title, urls: data.urls }) : [];
};

export default function Terms(): JSX.Element {
  return (
    <Layout>
      <section className="grow prose dark:prose-invert max-w-none section-first">
        <div className="container grid lg:grid-cols-3 items-start lg:gap-16">
          <div className="lg:sticky top-16">
            <div className="eyebrow">Legal</div>
            <h1 className="h2 mt-0 mb-4">Business User Agreement</h1>
            <p className="mt-0 mb-12">
              <i>Last updated: 2023-07-14</i>
            </p>
            <SideNav />
          </div>
          <motion.div variants={item} className="col-span-2 mt-8">
            <p>
              This Business User Agreement (
              <strong>
                <i>Agreement</i>
              </strong>
              ) is a contract between you (
              <strong>
                <i>you</i>
              </strong>{" "}
              or{" "}
              <strong>
                <i>your</i>
              </strong>
              ) and Brale Inc. (
              <strong>
                <i>we</i>
              </strong>
              ,{" "}
              <strong>
                <i>us</i>
              </strong>
              , or{" "}
              <strong>
                <i>Brale</i>
              </strong>
              ) that applies to your creation and use of an account (the{" "}
              <strong>
                <i>Account</i>
              </strong>
              ) to access any services that may be offered by Brale from time to
              time (the{" "}
              <strong>
                <i>Services</i>
              </strong>
              ). By signing up for an Account or using a Service, or by
              obtaining, holding, or using a Digital Asset issued by Brale, you
              agree that you have read, understand, and accept all of the terms
              and conditions contained in this Agreement, including our{" "}
              <Link prefetch="intent" to="/legal/privacy">
                Privacy Policy
              </Link>{" "}
              and{" "}
              <Link prefetch="intent" to="/legal/terms">
                Terms of Service
              </Link>
              . In the event of any conflict between the terms and conditions of
              this Agreement and any other terms and conditions, the terms and
              conditions of this Agreement shall control. In addition to this
              Agreement, you may have to agree to additional terms and
              conditions to use your Account or the Services. If you do not
              agree with any such modification, your sole and exclusive remedy
              is to terminate your use of the Services and close your Account.
            </p>
            <p>
              We may amend this Agreement at any time by posting a revised
              version on brale.xyz/legal or any other website we maintain for
              the purposes of providing the Services or Accounts. Amendments to
              this Agreement are effective at the time we post them and your
              continued use of your Account or the Services constitutes
              acceptance of any amendments. Accounts must be used in accordance
              with this Agreement.
            </p>
            <p>
              <strong>
                Brale is not registered with the U.S. Securities and Exchange
                Commission and does not offer securities services in the United
                States or to U.S. persons. You acknowledge that Digital Assets
                and fiat, if any, held in your Account are not subject to
                protections or insurance provided by the Federal Deposit
                Insurance Corporation or the Securities Investor Protection
                Corporation.
              </strong>
            </p>
            <p>
              <strong>
                Brale does not provide investment, tax, or legal advice. You
                should consult your legal or tax professional regarding your
                specific situation. Brale may provide educational information
                about Digital Currency through means that may include, but are
                not limited to, blog posts, articles, links to third-party
                content, news feeds, tutorials, and videos. The information
                provided on this website or any third-party sites does not
                constitute investment advice, financial advice, trading advice,
                or any other sort of advice, and you should not treat any of the
                website&apos;s content as such.
              </strong>
            </p>
            <p>
              <strong>
                PLEASE NOTE THAT SECTION 10 CONTAINS AN ARBITRATION CLAUSE AND
                CLASS ACTION WAIVER. BY AGREEING TO THIS AGREEMENT, YOU AGREE TO
                RESOLVE ALL DISPUTES, EXCEPT AS OTHERWISE SET FORTH IN SECTION
                10, THROUGH BINDING INDIVIDUAL ARBITRATION, WHICH MEANS THAT YOU
                WAIVE ANY RIGHT TO HAVE THE DISPUTE DECIDED BY A JUDGE OR JURY,
                AND YOU WAIVE ANY RIGHT TO PARTICIPATE IN COLLECTIVE ACTION,
                WHETHER THAT BE A CLASS ACTION, CLASS ARBITRATION, OR
                REPRESENTATIVE ACTION.
              </strong>
            </p>
            <ol>
              <li>
                <strong>IMPORTANT DEFINITIONS</strong>
                <p className="my-2">
                  <strong>
                    <i>Applicable Laws</i>
                  </strong>{" "}
                  means all applicable local, state, and federal statutes,
                  ordinances, rules, regulations, and other binding law in
                  effect from time to time, and all applicable orders,
                  judgments, decisions, rules, policies, opinions, attorney
                  general opinions, or guidelines passed or issued by any
                  Governmental Entity or any competent court, including, without
                  limitation, regarding U.S. securities laws and regulations,
                  Bank Secrecy Act/Anti-Money Laundering Laws, Applicable
                  Privacy Laws, state money transmission statutes, the DFS&apos;
                  statutes and regulations concerning virtual currency business
                  activity, Sanctions Laws, unclaimed property laws, the
                  Electronic Fund Transfer Act and its implementing regulations,
                  and any other applicable regulations or operating rules
                  relating to a Party or its users, as the case may be, or its
                  products and services.
                </p>
                <p className="my-2">
                  <strong>
                    <i>Applicable Privacy Laws</i>
                  </strong>{" "}
                  means all Applicable Laws concerning privacy, data security,
                  or data protection or personally identifiable information that
                  apply to you or Brale and their activities under this
                  Agreement, including the Gramm-Leach-Bliley Act and its
                  implementing regulations.
                </p>
                <p className="my-2">
                  <strong>
                    <i>Blockchain</i>
                  </strong>{" "}
                  means software operating a distributed ledger which is
                  maintained by a network of computers, and that records all
                  transactions in a Digital Asset in theoretically unchangeable
                  data packages known as blocks, each of which are timestamped
                  to reference the previous block so that the blocks are linked
                  in a chain that evidences the entire history of transactions
                  in the Digital Asset.
                </p>
                <p className="my-2">
                  <strong>
                    <i>Digital Asset</i>
                  </strong>{" "}
                  means any digital currency, app coin, stablecoin, or protocol
                  token that is accessible through a cryptographic protocol
                  maintained by a computer network.
                </p>
                <p className="my-2">
                  <strong>
                    <i>Private Key</i>
                  </strong>{" "}
                  means a string of data that permits access to a Smart Contract
                  or Digital Asset Wallet.
                </p>
                <p className="my-2">
                  <strong>
                    <i>Sanctions Laws</i>
                  </strong>{" "}
                  means any law or regulation related to economic sanctions
                  imposed, administered, or enforced by the Office of Foreign
                  Assets Control of the US Department of the Treasury, the US
                  Department of State, the European Union or any of its member
                  states, the United Nations, His Majesty&apos;s Treasury of the
                  United Kingdom, or any other governmental sanctions authority.
                </p>
                <p className="my-2">
                  <strong>
                    <i>Smart Contract</i>
                  </strong>{" "}
                  means an automated software program deployed on a Blockchain
                  that may be used to enforce or self-execute instructions
                  related to the creation, generation, or properties of a
                  Digital Asset.
                </p>
              </li>
              <li>
                <strong>CREATING YOUR ACCOUNT</strong>
                <ol type="a">
                  <li>
                    <strong>Eligibility.</strong> Accounts are available only to
                    incorporated businesses registered and located in the United
                    States where Brale is authorized to do business. By creating
                    an Account, you represent and warrant that (a) you are duly
                    organized and validly existing under the Laws of the
                    jurisdiction in which you are organized; (b) you have full
                    power and authority, and have obtained all approvals,
                    permissions and consents necessary, to enter into this
                    Agreement, to perform its obligations, and to grant the
                    rights hereunder; (c) this Agreement is legally binding upon
                    you and enforceable in accordance with its terms; (d) the
                    execution, delivery and performance of this Agreement does
                    not and will not conflict with any agreement, instrument,
                    judgment or understanding, oral or written, to which you are
                    a party or by which you may be bound; (e) your activities
                    and utilization of the Services shall be in compliance with
                    all Applicable Laws; (f) you have appropriate training,
                    sophistication, expertise and knowledge necessary to make
                    informed decision regarding trading in Digital Assets and
                    use of the Services; (g) you have provided accurate
                    information regarding the jurisdiction(s) in which you
                    operate and are incorporated and shall promptly notify Brale
                    of any change in such jurisdiction(s); (h) you are not an
                    individual or sole proprietor, and will not use the Services
                    for personal, family, or household purposes; and (i) you are
                    not engaged in Prohibited Activities (defined below).
                  </li>
                  <li>
                    <strong>Access and Account Setup.</strong> We may, in our
                    sole discretion, refuse to open an Account, suspend or
                    terminate any Account, and suspend or terminate all or a
                    portion of the Services. You agree that any information you
                    provide to Brale to create your Account is true and accurate
                    at the time you create your Account and you agree to update
                    that information from time to time to ensure it remains true
                    and accurate at all times. If you fail to update this
                    information, we may suspend or terminate your Account. You
                    authorize us to share this information in accordance with
                    our{" "}
                    <Link prefetch="intent" to="/legal/privacy">
                      Privacy Policy
                    </Link>
                    .
                  </li>
                  <li>
                    <strong>Access and Account Setup.</strong> The Services will
                    be provided only after your successful completion of the
                    Account acceptance process, as determined in our discretion.
                    To complete the acceptance process, you agree to provide us
                    with information and documents necessary for our compliance
                    with Applicable Laws, including without limitation the Bank
                    Secrecy Act and those relating to AML, KYC,
                    counter-terrorist financing, sanctions screening
                    requirements, or any other legal obligations, in each case,
                    as determined in our sole discretion. Upon your successful
                    completion of the Account acceptance process, we will
                    provide you with access privileges that permit you to access
                    the Services. You will identify a unique administrative
                    identifier that will be used to set up your Account. You
                    agree to limit access to your Account to authorized persons
                    within your organization.
                  </li>
                  <li>
                    <strong>Security.</strong> You are solely responsible for
                    the activity that occurs on your Account and for keeping
                    your Account passwords secure. You agree to never use
                    another person&apos;s Account or registration information or
                    to provide your Account or registration information to
                    another person or organization for any reason without our
                    written permission. You are responsible for the acts or
                    omissions of any person, including any authorized or
                    unauthorized person, who accesses your Account or the
                    Services using passwords or access procedures provided to or
                    created by you. You agree to promptly notify us of any
                    discovered or otherwise suspected breach of security or
                    unauthorized use of your Account.
                  </li>
                  <li>
                    <strong>Consent to Verification.</strong> To help the
                    government fight the funding of terrorism and money
                    laundering activities, federal law requires all financial
                    institutions to obtain, verify, and record information that
                    identifies each person who opens an Account. What this means
                    for you: When you create an Account, we will ask for your
                    name, address, date of birth, and other information that
                    will allow us to identify you. We may also ask to see a form
                    of identification with your photograph or other identifying
                    documents. You will need to complete verification procedures
                    before you are permitted to use the Services and may be
                    required to complete additional verification procedures at
                    any other time we deem necessary. You consent to us
                    accessing, processing, and retaining any information you
                    provide to us for the purpose of us providing the Services
                    to you. You can withdraw your consent at any time by closing
                    your Account with us, however we may retain and continue to
                    process your information if we reasonably believe it is
                    necessary in order to comply with Applicable Laws. You
                    confirm that any information you provide to us is accurate
                    and authentic and that you have the authorization to provide
                    it to us.{" "}
                    <strong>
                      You authorize us to make inquiries, whether directly or
                      through third parties, that we consider necessary to
                      verify your identity or protect you and/or us against
                      fraud or other financial crime, and to take action we
                      reasonably deem necessary based on the results of such
                      inquiries.
                    </strong>{" "}
                    We reserve the right to monitor, review, retain, and
                    disclose any information as necessary to satisfy any
                    Applicable Laws, regulation, sanctions programs, legal
                    process, or governmental request.
                  </li>
                  <li>
                    <strong>Acknowledgments.</strong> You agree and acknowledge
                    that (i) Brale has no control over the Blockchains and
                    markets in which Digital Assets are purchased and makes no
                    guarantee of their functionality, security, or availability;
                    (ii) the decentralized governance of Blockchains could,
                    among other things, lead to delays, conflicts of interest,
                    or operational decisions, including sudden changes in
                    operating rules, that may impact you or your Digital Assets;
                    and (iii) you are solely responsible for the fulfillment of
                    your obligations to your users, Brale is not the provider of
                    Services to your users through this Agreement and has no
                    liability to your users for any of the Services or any goods
                    or services that may be sold by you.
                  </li>
                </ol>
              </li>
              <li>
                <strong>USING YOUR ACCOUNT</strong>
                <ol type="a">
                  <li>
                    <strong>Hosted Digital Asset Wallet.</strong> Brale provides
                    qualifying Accounts access to a hosted wallet for storing
                    Digital Assets (a{" "}
                    <strong>
                      <i>Digital Asset Wallet</i>
                    </strong>
                    ). You may store supported Digital Assets in your hosted
                    Digital Asset Wallet. You retain ownership of such Digital
                    Assets. Brale maintains control over the Private Keys
                    associated with the Digital Asset Wallet. As owner of
                    supported Digital Assets in your Digital Asset Wallet, you
                    agree that you bear all risk of loss of such Digital Assets.
                    <p className="my-2">
                      Transfers from your Brale hosted Digital Asset Wallet are
                      executed at your instruction by us. Erroneously
                      transmitted funds will be lost. Brale shall bear no
                      liability or responsibility in the event your instructions
                      contain an incorrect Blockchain destination address or if
                      you send Digital Assets to an incompatible recipient
                      destination. Digital Asset transfers cannot be reversed.
                      We may refuse to process or cancel requested or pending
                      transfers as required by Applicable Laws. You agree that
                      we incur no obligation whatsoever with regard to
                      unsupported Digital Assets sent to a Brale hosted Digital
                      Asset Wallet and such Digital Assets may be lost. We may
                      in our sole discretion terminate support for any
                      particular Digital Asset at any time and without notice to
                      you. If you hold such Digital Asset in a Brale hosted
                      Digital Asset Wallet after we terminate our support for
                      such Digital Asset, then such Digital Assets may be lost
                      and you agree that Brale holds no liability with respect
                      to such loss. We may require you to wait some amount of
                      time before permitting you to engage in transactions
                      beyond certain volume limits.
                    </p>
                    <p className="my-2">
                      We have no duties or responsibilities with respect to
                      Brale hosted Digital Asset Wallets other than those that
                      are specifically set forth in this Agreement. No provision
                      of this Agreement shall require Brale to expend or risk
                      our own funds, property, or otherwise incur any financial
                      liability, in the performance of our duties hereunder.
                    </p>
                  </li>
                  <li>
                    <strong>Digital Asset Issuance and Redemption.</strong>{" "}
                    Brale provides qualifying Accounts access to purchase and
                    redeem supported Digital Assets. We may in our sole
                    discretion terminate support to purchase and/or redeem any
                    Digital Asset. We reserve the right to delay or halt
                    purchases and redemptions to comply with Applicable Laws, in
                    times of unusual market conditions, or for any other reason
                    in our sole discretion.
                    <p className="my-2">
                      Brale shall not be liable for any losses resulting from or
                      arising out of delays in processing transactions,
                      inability to execute transactions, or lack of timely
                      response from customer support. Brale shall not be liable
                      for any alleged losses that you suffer from a drop in
                      Digital Asset values or prices.
                    </p>
                  </li>
                  <li>
                    <strong>Digital Asset Creation.</strong> Brale provides
                    qualifying Accounts the ability to create new Digital Assets
                    on supported Blockchains (each, a{" "}
                    <strong>
                      <i>Creator Digital Asset</i>
                    </strong>
                    ). We may, in our sole discretion, make any Creator Digital
                    Assets available to you and/or other Account holders in
                    connection with Services offered to them by Brale. To the
                    extent permitted by Applicable Law, we have no duties or
                    responsibilities to maintain any Creator Digital Asset and
                    may modify the properties and availability of any Creator
                    Digital Asset at any time at our discretion. You agree and
                    acknowledge that by creating a Creator Digital Asset, Brale
                    shall take all actions it determines are necessary,
                    including deploying or revising one or more Smart Contracts
                    to supported Blockchains in a manner that may be
                    irreversible.
                  </li>
                  <li>
                    <strong>Digital Asset Reserves.</strong> Brale provides
                    qualifying Accounts the ability to store certain assets that
                    back the value of Creator Digital Assets (
                    <strong>
                      <i>Reference Assets</i>
                    </strong>
                    ). All Reference Assets are generally held in one or more
                    accounts titled in the name of Brale. You acknowledge that
                    Brale may, in its sole discretion, hold Reference Assets in
                    various instruments, and that you are not entitled to any
                    returns that may be earned on such instruments. To the
                    extent permitted by Applicable Law, Brale shall not be
                    responsible for the maintenance of Reference Assets and
                    shall not be responsible for any loss of Reference Assets
                    for any reason.
                    <p className="my-2">
                      By using the Services to store Reference Assets, you
                      acknowledge that Brale may hold those Reference Assets
                      until such time as the related Creator Digital Assets are
                      tendered to us. If Brale determines, in its sole
                      discretion, that it is unable to continue storing
                      Reference Assets for your Creator Digital Assets, you
                      shall make arrangements to ensure the ability to continue
                      to perform redemptions such that there is a continuity of
                      service for redemptions in compliance with Applicable Law.
                    </p>
                  </li>
                  <li>
                    <strong>Transaction Limits.</strong> Brale reserves the
                    right to establish and change the deposit, withdrawal,
                    storage, transfer, and velocity limits on your Account that
                    are applicable to any usage of your Account or the Services
                    as we deem necessary.
                  </li>
                  <li>
                    <strong>Support.</strong> You may contact our support team
                    at {EMAILS.support} with any requests or questions related
                    to your Account or the Services, or to report any violations
                    of this Agreement.
                  </li>
                  <li>
                    <strong>Terminating Your Account.</strong> If you desire to
                    terminate your Account, please contact {EMAILS.support}. If
                    your Account holds a balance, we may require that you
                    provide instructions as to where to transfer that balance
                    prior to closing your Account.
                  </li>
                  <li>
                    <strong>Dormant Accounts.</strong> If you terminate your
                    Account and fail to provide instructions for any remaining
                    balance, we may be required by Applicable Law to deem the
                    funds to be abandoned after a certain period of inactivity
                    (a{" "}
                    <strong>
                      <i>Dormant Account</i>
                    </strong>
                    ). Where required by Applicable Law, we will attempt to
                    notify you if we believe your Account is a Dormant Account.
                    If you do not respond to such attempts to notify you by
                    providing instructions as to how to handle your remaining
                    balance, we will deliver them to the appropriate government
                    authority. If we do not have sufficient information from you
                    to determine your state of residence, we will deliver your
                    balance to the Iowa Treasurer of State.
                  </li>
                </ol>
              </li>
              <li>
                <strong>PAYMENTS</strong>
                <ol type="a">
                  <li>
                    <strong>Fees.</strong> Brale may charge fees in connection
                    with your usage of your Account or the Services. A
                    description of the fees charged by Brale can be found within
                    your Account. By holding an Account and using our Services,
                    you agree to pay all applicable fees and expenses, including
                    any additional taxes, levies, duties or similar governmental
                    assessments of any nature (
                    <strong>
                      <i>Tax</i>
                    </strong>
                    ). If we determine that we have a legal obligation to pay or
                    collect Tax for which you are responsible, you agree to pay
                    this amount to us. We reserve the right to adjust our
                    pricing and fees at any time and may net these fees out of
                    transfers to or from Brale at our discretion.
                  </li>
                  <li>
                    <strong>Payment Terms.</strong> All amounts due under this
                    Agreement shall be paid in full (without deduction, set-off
                    or counterclaim) within ten (10) days of when we bill you,
                    payable in US dollars. If payment of any amounts due
                    hereunder are not made when due and payable, a late fee will
                    accrue at the rate of the lesser of one and one-half percent
                    (1.5%) per month or the highest legal rate permitted by
                    Applicable Laws and you will pay all reasonable expenses of
                    collection. If any past due payment has not been received by
                    Brale within ten (10) days from the time such payment is
                    due, Brale may suspend your access to your Account and/or
                    the Services until such payment is made.
                  </li>
                  <li>
                    <strong>Right to Set Off.</strong> You agree that Brale is
                    authorized, without prior notice and both before and after
                    demand, to set off the whole or any part of your liabilities
                    or other amounts payable to Brale, against any sums held by
                    Brale and owed to you, whether under this Agreement or any
                    other agreement between Brale and you. You agree that (i)
                    Brale is entitled to convert or exchange any Digital Assets
                    owned by you and held by Brale, and is authorized to effect
                    any such conversions at the then prevailing exchange rate;
                    and (ii) if your liability is contingent and/or
                    unliquidated, then Brale may set off the amount it estimates
                    in good faith will be the liquidated amount.
                  </li>
                </ol>
              </li>
              <li>
                <strong>UNAUTHORIZED TRANSACTIONS AND ERROR RESOLUTION</strong>
                <ol type="a">
                  <li>
                    <strong>Compliance with Laws.</strong> You shall only use
                    your Account and the Services for lawful and legitimate
                    purposes and you will at all times comply with all
                    Applicable Laws.
                  </li>
                  <li>
                    <strong>Prohibited Activities.</strong> You shall not use
                    your Account or the Services to directly or indirectly: (i)
                    engage in illegal or fraudulent activities; (ii) benefit any
                    country, organization, entity, or person embargoed or
                    blocked by any government, including those on the sanctions
                    lists maintained by the U.S. Office of Foreign Asset Control
                    (OFAC); or (iii) engage in transactions that are illegal,
                    unethical, or that Brale reasonably believes could harm
                    Brale&apos;s reputation, business, or interests (in
                    aggregate,{" "}
                    <strong>
                      <i>Prohibited Activities</i>
                    </strong>
                    ).
                    <p className="my-2">
                      In addition to the above Prohibited Activities, Brale
                      reserves the right to suspend your Account or terminate
                      this Agreement in the event that you are using your
                      Account or the Services to conduct any activity we
                      determine to be high risk or to expose us or other Brale
                      clients to an unacceptable level of risk. If we learn or
                      suspect that you are using your Account or the Services
                      for any illegal or fraudulent purpose, we may share your
                      information with any government or regulatory authority,
                      financial institution, or law enforcement agency we deem
                      necessary to fulfill our obligations to comply with
                      Applicable Laws.
                    </p>
                  </li>
                  <li>
                    <strong>Notification of Account Errors.</strong> Contact us
                    immediately at support@brale.xyz if you believe that (i)
                    your Account has been accessed without your authorization;
                    (ii) a transaction has occurred that you did not authorize;
                    (iii) a transaction has been processed incorrectly to or
                    from your Account; or (iv) your Account transaction history
                    made available to you contains an error (each, an{" "}
                    <strong>
                      <i>Error</i>
                    </strong>
                    ). For clarity, if you give a third party access to your
                    Account and that person conducts transactions you did not
                    anticipate, these transactions are not Errors. You must
                    notify us within fourteen (14) days after the Error first
                    appeared in your transaction history. If you do not notify
                    us within fourteen (14) days, then you will not be covered
                    by the protections of this Section 5.
                  </li>
                  <li>
                    <strong>Required Information for Notice.</strong> When you
                    notify us of the Error, you must provide: (i) Your name;
                    (ii) the nature of the suspected Error and why you believe
                    it is an Error; and (iii) the dollar amount of the Error. In
                    connection with investigating an Error, we may need to
                    request further information from you regarding the
                    circumstances of the Error. If you do not provide such
                    additional information, it may inhibit our ability to
                    resolve your issue or limit your rights under this Section
                    5.
                  </li>
                  <li>
                    <strong>Investigation of Errors.</strong> We will typically
                    determine whether an Error occurred within ten (10) business
                    days after you notify us and provide all required
                    information in Section 5.4. If we need more time, we may
                    take up to 45 days to investigate but we will credit your
                    Account within ten (10) business days for the amount of the
                    suspected error. For Errors involving new Accounts, we may
                    take up to 90 days to investigate and may take up to 20
                    business days to credit your Account. We will tell you the
                    results of our investigation within 3 business days after
                    completing our investigation. If we determine there was no
                    Error, we will provide you a written explanation. If we have
                    credited your Account and determine that your claim of an
                    Error is not valid, the amount credited will be debited from
                    your Account.
                  </li>
                </ol>
              </li>
              <li>
                <strong>YOUR LIABILITY</strong>
                <ol type="a">
                  <li>
                    <strong>Your Liability.</strong> You are responsible for all
                    reversed or failed transactions, fees, claims, fines,
                    penalties, and other liabilities incurred by Brale arising
                    from your use of your Account or the Services or any breach
                    of this Agreement. Without limiting the foregoing, you agree
                    to reimburse Brale for any liability relating to a
                    transaction that: (i) is reversed in accordance with our
                    risk management policies; (ii) is reversed in accordance
                    with Applicable Laws, including without limitation, by ACH
                    return (as that term is defined under the Nacha Rules),
                    (iii) fails due to your error; (iv) fails due to the
                    provision of inaccurate information; or (v) is canceled or
                    rejected by you or the recipient or sender, or your or their
                    bank or credit union (each, a{" "}
                    <strong>
                      <i>Reversal</i>
                    </strong>
                    ).
                  </li>
                  <li>
                    <strong>Actions We May Take.</strong> To recover any amounts
                    lost due to a Reversal, you authorize Brale to debit your
                    Account for such amount. Additionally, at our option, we may
                    suspend your Account or engage in collection activities. For
                    all other liabilities or if Brale determines, in our sole
                    discretion, that you have breached this Agreement or that
                    your Account or use of the Services presents a risk or other
                    threat to Brale, Brale&apos;s clients, or the Brale
                    Services, or if we are unable to verify your identity, we
                    may take any of the following actions (without limiting or
                    foregoing any other remedies available to us under the law
                    or the terms of this Agreement):
                    <ul>
                      <li>
                        Suspending your access to your Account or the Services;
                      </li>
                      <li>Closing your Account;</li>
                      <li>
                        Contacting your bank or credit union, contacting law
                        enforcement or other third parties impacted by your
                        activities;
                      </li>
                      <li>and Taking legal action against you.</li>
                    </ul>
                  </li>
                </ol>
              </li>
              <li>
                <strong>CONSENT TO RECEIVE ELECTRONIC COMMUNICATIONS</strong>
                <ol type="a">
                  <li>
                    <strong>Communications.</strong> By opening an Account or
                    using the Services, you consent to receive all
                    communications, terms, disclosures, notices, and statements
                    (Communications) from Brale electronically and you confirm
                    that you can access, receive and retain such Communications.
                    Brale will provide Communications by posting them on
                    brale.xyz or by emailing them to you at the email address
                    you provided when opening or updating your Account.
                  </li>
                  <li>
                    <strong>Receipt of Notices.</strong> A Communication to you
                    will be deemed to have been received twenty-four hours after
                    we have posted it to our website or emailed it to you.
                  </li>
                  <li>
                    <strong>Delivery Requirements.</strong> In order to access
                    and retain our Communications you will need: a computer or
                    mobile device that is Internet-enabled, a valid email
                    address, Internet connectivity, and a web browser which
                    includes 128-bit encryption, with cookies and javascript
                    enabled.
                  </li>
                  <li>
                    <strong>Withdrawing Consent.</strong> You may withdraw your
                    consent to receive Communications electronically by
                    contacting us at {EMAILS.support}. Because the nature of the
                    Services requires delivery of Communications electronically,
                    we may close your Account upon receipt of notice of your
                    intent to withdraw such consent.
                  </li>
                </ol>
              </li>
              <li>
                <strong>INTELLECTUAL PROPERTY</strong>
                <ol type="a">
                  <li>
                    <strong>Covered Intellectual Property.</strong> All rights
                    in intellectual property owned by Brale or its partners (
                    <strong>
                      <i>Covered Intellectual Property</i>
                    </strong>
                    ) not expressly granted to you in this Agreement are
                    reserved. You shall not and shall not allow any third party
                    to use your Account or the Services to: (i) claim or
                    register any Covered Intellectual Property on your behalf or
                    on behalf of others; (ii) import or export any portion of
                    the Services to a person or country in violation of export
                    control laws; (iii) copy, reproduce, post, transmit, resell
                    or distribute the Services or any content or data obtained
                    through the Services or our website; (iv) access or attempt
                    to access non-public Brale information; (v) hack or develop
                    workarounds to any technical limitations placed on your
                    Account; (vi) reverse engineer the Services; (vii) engage in
                    any activity that would interfere with or affect
                    Brale&apos;s ability to provide the Services, including
                    sending an unreasonably large number of requests in quick
                    succession; or (viii) attempt any of the foregoing.
                  </li>
                  <li>
                    <strong>License to Brale.</strong> You grant Brale a
                    worldwide, non-exclusive, royalty-free license to (a) use,
                    copy, access, process, reproduce, perform, display,
                    distribute and transmit the name, logos, and trademarks (i)
                    of you; and (ii) those provided by you in the creation of
                    any Creator Digital Assets (collectively,{" "}
                    <strong>
                      <i>Your Marks</i>
                    </strong>
                    ) for the purpose of providing the Services to you,
                    including, without limitation, to display the Marks within
                    the your Account and on or in connection with Digital Assets
                    issued using the Services and (b) include Your Marks within
                    lists of those utilizing Brale&apos;s services, both on
                    Brale&apos;s public-facing website and in marketing and
                    promotional materials.
                  </li>
                  <li>
                    <strong>Digital Assets.</strong> You acknowledge and agree
                    that Brale owns all rights in and to any Smart Contracts
                    deployed pursuant to this Agreement and their associated
                    Private Keys, and no license or rights are granted to you to
                    the same through this Agreement, by implication, estoppel,
                    or otherwise.
                  </li>
                  <li>
                    <strong>Marketing.</strong> Unless otherwise agreed to in
                    writing by Brale, you agree not to use or refer to Creator
                    Digital Assets, Brale, its trademarks, service marks, trade
                    names, logos, or any other intellectual property in any
                    internal or external communications, including but not
                    limited to your website, social media accounts, marketing
                    materials, presentations, reports, or any other form of
                    online or offline communication. This restriction includes,
                    but is not limited to, using such intellectual property in
                    any manner that may suggest an endorsement, sponsorship, or
                    partnership with Brale. No part of this Agreement shall be
                    construed as providing such agreement, in part or in whole.
                  </li>
                </ol>
              </li>
              <li>
                <strong>TERMINATION</strong>
                <ol type="a">
                  <li>
                    <strong>By You.</strong> You may stop using your Account at
                    any time. You may terminate this Agreement by notifying us
                    at {EMAILS.support} that you intend to close your Account.
                  </li>
                  <li>
                    <strong>By Brale.</strong> We may close your Account or
                    terminate your access to the Services without liability, for
                    any reason, upon notice to you. Brale reserves the right to
                    modify or terminate any portion of or all of the Services at
                    any time, for any reason.
                  </li>
                  <li>
                    <strong>Effects of Termination.</strong> Upon any
                    termination of this Agreement or your access to the
                    Services, or closure of your Account, Brale (i) shall retain
                    all Private Keys and shall not share the same with you, and
                    (ii) may retain your data as required by Applicable Law.{" "}
                    <strong>
                      For clarity, as a result of Brale&apos;s retention of
                      Private Keys, you may continue to use Digital Assets in
                      their existing state upon termination, but may not modify,
                      update, amend the functionality of, or issue new or
                      existing Digital Assets or Smart Contracts, including for
                      greater certainty those related to any Creator Digital
                      Assets created by you.
                    </strong>
                  </li>
                </ol>
              </li>
              <li>
                <strong>DISPUTES</strong>
                <ol type="a">
                  <li>
                    <strong>Binding Arbitration.</strong> You and Brale (the{" "}
                    <strong>
                      <i>Parties</i>
                    </strong>
                    ) agree that any past, present, or future dispute, claim or
                    controversy relating in any way to your Account or use of
                    the Services, or any other relationship or transaction
                    between you and us, will be resolved by binding arbitration
                    as discussed below, and not through litigation in any court.
                    Such disputes are called{" "}
                    <strong>
                      <i>Claims</i>
                    </strong>{" "}
                    for purposes of this agreement. This binding agreement to
                    arbitrate applies regardless of whether the dispute is
                    between you and us jointly or separately. This arbitration
                    agreement is entered into pursuant to the Federal
                    Arbitration Act, 9 U.S.C. §§ 1-16. The arbitration shall be
                    conducted by a single neutral arbitrator acting under the
                    administration of United States Arbitration and Mediation,
                    MidWest, Inc., and in accordance with the then-applicable
                    United States Arbitration and Mediation Rules of
                    Arbitration. Unless the Parties agree otherwise, or unless
                    the arbitration administrator&apos;s rules or law require
                    otherwise, the arbitration shall be held in Des Moines, IA.
                    The arbitrator will decide the Claim in accordance with all
                    Applicable Laws, including recognized principles of equity
                    and statutes of limitations, and will honor all privileges
                    recognized by Applicable Laws. The arbitrator will have the
                    authority to award to a Party any damages or relief provided
                    for under Applicable Laws. The arbitrator&apos;s decision
                    shall be final and legally binding, and may be enforced by
                    any court having jurisdiction. If you elect to proceed with
                    respect to any Claim by an individual action in a small
                    claims court, or its equivalent, instead of arbitration, we
                    will not object, however, any such action shall be taken in
                    small claims court in Des Moines, Iowa. If the arbitrator or
                    arbitration administrator would impose filing fees or other
                    administrative costs on you, we will reimburse you, upon
                    request, to the extent such fees or costs would exceed those
                    that you would otherwise have to pay if you were proceeding
                    instead in a court. We will also pay additional fees or
                    costs if required to do so by the arbitration
                    administrator&apos;s rules or Applicable Laws. Apart from
                    the foregoing, each Party will be responsible for any other
                    fees or costs, such as attorney fees that the Party may
                    incur. If you consider that you are unable to afford any
                    fees or costs that would be yours to pay, you may request
                    that we pay or reimburse them, and we will consider your
                    request in good faith.{" "}
                    <strong>
                      <span className="underline underline-offset-2">
                        THE PARTIES WAIVE ANY CONSTITUTIONAL AND STATUTORY
                        RIGHTS TO HAVE A TRIAL IN FRONT OF A JUDGE OR A JURY.
                      </span>{" "}
                      ARBITRATION WITH RESPECT TO A CLAIM IS BINDING AND NEITHER
                      PARTY WILL HAVE THE RIGHT TO LITIGATE THAT CLAIM THROUGH A
                      COURT. IN ARBITRATION THE PARTIES WILL NOT HAVE THE SAME
                      RIGHTS THAT APPLY IN COURT, SUCH AS THE RIGHT TO A TRIAL
                      BY JUDGE OR JURY AND THE RIGHT TO PARTICIPATE OR BE
                      REPRESENTED IN PROCEEDINGS BROUGHT BY OTHERS SUCH AS CLASS
                      ACTIONS OR SIMILAR PROCEEDINGS. IN ADDITION, THE RIGHT TO
                      DISCOVERY AND THE RIGHT TO APPEAL MAY ALSO BE LIMITED OR
                      ELIMINATED IN ARBITRATION. ALL OF THESE JUDICIAL RIGHTS
                      ARE WAIVED WITH RESPECT TO CLAIMS.
                    </strong>
                  </li>
                  <li>
                    <strong>Class Waiver; Waiver of Trial by Jury.</strong>{" "}
                    <strong>
                      TO THE FULLEST EXTENT PERMITTED BY LAW, THE PARTIES AGREE
                      THAT NO CLASS ACTIONS OR SIMILAR PROCESS, AND NO JOINDER
                      OR CONSOLIDATION OF ANY CLAIM WITH A CLAIM OF ANY OTHER
                      PERSON OR ENTITY, SHALL BE ALLOWABLE.
                    </strong>
                    In arbitration, the arbitrator shall have no authority to
                    entertain any Claim on behalf of a class, group, person, or
                    entity who is not a named party to the arbitration, nor
                    shall any arbitrator have authority to make any award for
                    the benefit of, or against, any class, group, person, or
                    entity who is not a named party to the arbitration. In the
                    event that there is a dispute about whether limiting
                    arbitration to non-class proceedings, or to the named
                    parties, is enforceable under Applicable Laws, then that
                    question shall be resolved by a court rather than by an
                    arbitrator; and to the extent it is determined that
                    resolution of a Claim must proceed on a class basis, it
                    shall so proceed in a court of competent jurisdiction rather
                    than in arbitration.{" "}
                    <strong>
                      IN THE EVENT THAT ANY DISPUTE PROCEEDS IN COURT, EACH
                      PARTY IRREVOCABLY WAIVES ANY RIGHT TO TRIAL BY JURY IN ANY
                      PROCEEDING WITH REGARD TO ANY CLAIM.
                    </strong>
                  </li>
                </ol>
              </li>
              <li>
                <strong>GENERAL</strong>
                <ol type="a">
                  <li>
                    <strong>Indemnity.</strong> You agree to defend and hold
                    harmless Brale and our respective officers, directors,
                    agents, employees, affiliates, and suppliers (the{" "}
                    <strong>
                      <i>Indemnified Parties</i>
                    </strong>
                    ) against any and all claims, actions, proceedings, and
                    suits by a third party or any inquiry, investigation, or
                    proceeding brought by a regulatory or other governmental
                    body arising from: (a) your violation of this Agreement or
                    any other applicable terms or policies of Brale; (b) your
                    use of your Account or the Services; (c) our authorized use
                    of your data or execution of directions issued by you to us;
                    (d) our authorized use of Your Marks; (e) your creation of
                    any Creator Digital Assets (f) disputes related to any
                    acknowledgments made by you in this Agreement; (g) your
                    breach of any representation or warranty set forth in this
                    Agreement; (h) your negligence or willful misconduct; and/or
                    (i) your actual or alleged violation of any third party
                    rights, or any Applicable Laws, regulations or rules, and to
                    indemnify the Indemnified Parties for settlement amounts or
                    damages, liabilities, costs, and expenses (including
                    reasonable attorneys&apos; fees) awarded and arising out of
                    such claims.
                  </li>
                  <li>
                    <strong>Limitation of Liability.</strong> IN NO EVENT SHALL
                    BRALE, OUR AFFILIATES, OR OUR OR THEIR RESPECTIVE OFFICERS,
                    DIRECTORS, AGENTS, EMPLOYEES, OR SUPPLIERS BE LIABLE TO YOU
                    OR ANY THIRD PARTY UNDER ANY CIRCUMSTANCES FOR ANY INDIRECT,
                    CONSEQUENTIAL, SPECIAL, PUNITIVE, OR EXEMPLARY DAMAGES OR
                    LOSSES, INCLUDING BUT NOT LIMITED TO DAMAGES FOR LOSS OF
                    PROFITS, GOODWILL, USE, DATA, OR OTHER INTANGIBLE LOSSES
                    INCURRED IN CONNECTION WITH: (A) THIS AGREEMENT, (B) YOUR
                    USE OF, INABILITY TO USE, OR UNAVAILABILITY OF YOUR ACCOUNT
                    OR THE SERVICES, OR THE COST OF PROCURING SUBSTITUTE
                    TECHNOLOGY, GOODS OR SERVICES, OR (C) ANY GOODS OR SERVICES
                    PURCHASED, RECEIVED, OR PAID FOR USING THE SERVICES, UNDER
                    ANY THEORY OF LIABILITY OR CAUSE OF ACTION WHETHER IN TORT
                    (INCLUDING NEGLIGENCE), CONTRACT, OR OTHERWISE, REGARDLESS
                    OF WHETHER BRALE OR OUR AFFILIATES HAVE BEEN ADVISED OF THE
                    POSSIBILITY OF SUCH DAMAGES. IN NO EVENT SHALL THE LIABILITY
                    OF BRALE OR OUR AFFILIATES AND THE LIABILITY OF OUR AND
                    THEIR RESPECTIVE OFFICERS, DIRECTORS, AGENTS, EMPLOYEES, AND
                    SUPPLIERS EXCEED THE FEES BRALE HAS RECEIVED FROM YOU
                    THROUGH YOUR USE OF THE SERVICES DURING THE PREVIOUS 12
                    MONTHS. BRALE AND OUR AFFILIATES SHALL NOT BE JOINTLY LIABLE
                    FOR ANY MATTERS HEREUNDER. THIS LIMITATION OF LIABILITY
                    SECTION SHALL APPLY TO FULLEST EXTENT PERMITTED BY LAW IN
                    THE APPLICABLE JURISDICTION.
                  </li>
                  <li>
                    <strong>No Warranty.</strong> YOUR ACCOUNT AND THE SERVICES
                    ARE PROVIDED “AS IS” AND “AS AVAILABLE”, WITHOUT ANY
                    REPRESENTATION OF WARRANTY, WHETHER EXPRESS, IMPLIED, OR
                    STATUTORY. USE OF THE SERVICES IS AT YOUR OWN RISK. BRALE ,
                    OUR AFFILIATES, AND OUR AND THEIR RESPECTIVE OFFICERS,
                    DIRECTORS, AGENTS, EMPLOYEES, AND SUPPLIERS, SPECIFICALLY
                    DISCLAIM ANY IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS
                    FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. NEITHER
                    BRALE NOR OUR AFFILIATES REPRESENTS OR WARRANTS THAT YOUR
                    ACCOUNT OR THE SERVICES WILL MEET YOUR REQUIREMENTS, BE
                    CONTINUOUS, UNINTERRUPTED, SECURE, TIMELY, OR ERROR-FREE, OR
                    THAT DEFECTS WILL BE CORRECTED. NO ADVICE OR INFORMATION,
                    WHETHER ORAL OR WRITTEN, OBTAINED BY YOU FROM BRALE WILL
                    CREATE ANY WARRANTY NOT EXPRESSLY STATED HEREIN. NEITHER
                    BRALE NOR OUR AFFILIATES SHALL BE RESPONSIBLE FOR ANY
                    SERVICE INTERRUPTIONS OR SYSTEM FAILURES THAT MAY AFFECT THE
                    PROCESSING, COMPLETION, OR SETTLEMENT OF TRANSACTIONS. THIS
                    DISCLAIMER OF WARRANTY SECTION SHALL APPLY TO THE FULLEST
                    EXTENT PERMITTED BY LAW IN THE APPLICABLE JURISDICTION.
                  </li>
                  <li>
                    <strong>Force majeure.</strong> In the event that Brale is
                    prevented from performing, or is unable to perform, any of
                    its obligations under this Agreement due to any cause beyond
                    its reasonable control including, without in any way
                    limiting the generality of the foregoing, fire, explosion,
                    earthquake, storm, flood, strike, war, insurrection, riot,
                    act of God or the public enemy, failures in any
                    telecommunications, network or other service or equipment
                    that are not within our reasonable control, unauthorized
                    access, breach of firewalls or other hacking by third
                    parties, instructions of Government or other public
                    authorities, or judgment or decree of a court of competent
                    jurisdiction (not arising out of breach by such party of
                    this Agreement), our performance shall be excused or
                    extended for the period of delay or inability to perform due
                    to such occurrence. In such an event, Brale may suspend or
                    terminate your Account and your access to the Services.
                  </li>
                  <li>
                    <strong>Assignment.</strong> You may not transfer or assign
                    any rights or obligations you have under this Agreement, in
                    whole or in part. Brale reserves the right to transfer or
                    assign any or all rights or obligations under this Agreement
                    at any time. This Agreement shall be binding upon, and inure
                    to the benefit of, the successors, representatives and
                    permitted assigns of each party hereto.
                  </li>
                  <li>
                    <strong>Governing Law.</strong> You agree that this
                    Agreement shall be governed by and construed in accordance
                    with the laws of the State of Delaware, USA, without regard
                    to its conflicts of law provisions. The United Nations
                    Convention on Contracts for the International Sale of Goods
                    shall not apply to this Agreement. Unless waived by Brale in
                    its sole discretion, exclusive jurisdiction and venue for
                    actions related to this Agreement will be the state or
                    federal courts located in New Castle County, Delaware, and
                    both parties consent to the jurisdiction of such courts with
                    respect to any such action. In any action or proceeding to
                    enforce this Agreement, the prevailing party will be
                    entitled to recover from the other party its costs and
                    expenses (including reasonable attorneys&apos; fees)
                    incurred in connection with such action or proceeding and
                    enforcing any judgment or order obtained.
                  </li>
                  <li>
                    <strong>Complete Agreement; No Waiver.</strong> This
                    Agreement, along with any applicable terms and policies of
                    Brale, constitutes the entire agreement, and supersedes all
                    prior negotiations, understandings or agreements (oral or
                    written), between the parties regarding the subject matter
                    of this Agreement (and all past dealing or industry custom).
                    The failure of either party to enforce its rights under this
                    Agreement at any time for any period will not be construed
                    as a waiver of such rights, and the exercise of one right or
                    remedy will not be deemed a waiver of any other right or
                    remedy. If any provision of this Agreement is determined to
                    be illegal or unenforceable, that provision will be limited
                    or eliminated to the minimum extent necessary so that this
                    Agreement will otherwise remain in full force and effect and
                    enforceable. This Agreement is in English only, which
                    language shall be controlling in all respects.
                  </li>
                  <li>
                    <strong>Survival.</strong> Sections 4 (Payments), 6 (Your
                    Liability), 7 (Consent to Receive Electronic
                    Communications), 8 (Intellectual Property), 10 (Disputes),
                    and 11 (General), as well as any other terms which by their
                    nature should survive, will survive the termination of this
                    Agreement.
                  </li>
                </ol>
              </li>
            </ol>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
